import React from 'react';

// Microphone icon — same shape used in the landing page (public/landing.html
// .s-mic-btn), so the app and the marketing site look consistent.
// Sized in `em` and filled with currentColor, so existing font-size / color
// styles on the parent control its size and colour.
export function MicIcon({ style }) {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: '-0.125em', ...style }}>
      <rect x="9" y="2" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0h-1.6a5.4 5.4 0 0 1-10.8 0H5z" />
      <rect x="11" y="18" width="2" height="3.2" rx="1" />
    </svg>
  );
}

// Pencil (edit) icon — same stroke style as the share envelope on event
// cards and the bottom-nav icons, replacing the ✏️ emoji.
export function PencilIcon({ style, size }) {
  return (
    <svg viewBox="0 0 24 24" width={size || '1em'} height={size || '1em'} fill="none" stroke="currentColor"
      strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: '-0.125em', ...style }}>
      <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
    </svg>
  );
}

// Trash (delete) icon — same stroke family, replacing the 🗑️ emoji.
export function TrashIcon({ style, size }) {
  return (
    <svg viewBox="0 0 24 24" width={size || '1em'} height={size || '1em'} fill="none" stroke="currentColor"
      strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: '-0.125em', ...style }}>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  );
}

// Paperclip (attach) icon — same stroke family as pencil/trash, for the
// attachment button in the event form's round action stack.
export function ClipIcon({ style, size }) {
  return (
    <svg viewBox="0 0 24 24" width={size || '1em'} height={size || '1em'} fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: '-0.125em', ...style }}>
      <path d="M21.44 11.05l-9.19 9.19a5 5 0 0 1-7.07-7.07l9.19-9.19a3.5 3.5 0 0 1 4.95 4.95l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

// Bell (reminder alarm) icon — same stroke family as pencil/trash/clip.
export function BellIcon({ style, size }) {
  return (
    <svg viewBox="0 0 24 24" width={size || '1em'} height={size || '1em'} fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: '-0.125em', ...style }}>
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

// Stop (recording) icon — a rounded square, paired with the mic.
export function StopIcon({ style }) {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: '-0.125em', ...style }}>
      <rect x="6" y="6" width="12" height="12" rx="2.5" />
    </svg>
  );
}

// Speaker icon — same filled, clean style as the mic, for the "Listen" (read
// aloud / TTS) button, so it matches the mic/camera set instead of an emoji.
export function SpeakerIcon({ style }) {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: '-0.125em', ...style }}>
      <path d="M3 9v6h4l5 5V4L7 9H3z" />
      <path d="M14 3.23v2.06a7 7 0 0 1 0 13.42v2.06a9 9 0 0 0 0-17.54z" />
      <path d="M14 7.97v8.06a4.5 4.5 0 0 0 0-8.06z" />
    </svg>
  );
}

// Planner icon — calendar page with time-slot lines, same filled style as the
// speaker. Used on the round PLAN button that opens the hourly grid.
export function PlannerIcon({ style }) {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: '-0.125em', ...style }}>
      <path fillRule="evenodd" clipRule="evenodd"
        d="M7 2a1 1 0 0 1 1 1v1h8V3a1 1 0 1 1 2 0v1h1a3 3 0 0 1 3 3v11a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h1V3a1 1 0 0 1 1-1zm13 7H4v9a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9z" />
      <rect x="6" y="11.2" width="12" height="1.7" rx="0.85" />
      <rect x="6" y="14.4" width="8" height="1.7" rx="0.85" />
      <rect x="6" y="17.6" width="10" height="1.7" rx="0.85" />
    </svg>
  );
}

// List icon — shown on the same round button while the planner is open,
// meaning "back to the list".
export function ListIcon({ style }) {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: '-0.125em', ...style }}>
      <circle cx="4.5" cy="6" r="1.6" />
      <circle cx="4.5" cy="12" r="1.6" />
      <circle cx="4.5" cy="18" r="1.6" />
      <rect x="8" y="5" width="13" height="2" rx="1" />
      <rect x="8" y="11" width="13" height="2" rx="1" />
      <rect x="8" y="17" width="13" height="2" rx="1" />
    </svg>
  );
}

// Camera icon — same filled, clean style as the mic. The lens is punched out
// with the even-odd fill rule so the hole shows the button's background through,
// keeping it crisp on any colour.
export function CameraIcon({ style }) {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: '-0.125em', ...style }}>
      <path fillRule="evenodd" clipRule="evenodd"
        d="M9.2 4h5.6l1.2 2H20a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h2l1.2-2zM12 9.2a3.8 3.8 0 1 0 0 7.6 3.8 3.8 0 0 0 0-7.6z" />
    </svg>
  );
}

// Back — the green circle on the event form, Notes and Search.
//
// Drawn rather than typed. The character "←" carries its own baseline offset,
// so it sits low inside a circle however the box is centred, and its weight
// comes from whatever font the phone happens to use — bolder than every other
// icon in the app on some, thinner on others. An SVG is centred by its own
// viewBox and takes the same stroke as the rest: 2.
export function BackIcon({ size = 20, style }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={style} aria-hidden="true">
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  );
}
